/**
 * reCAPTCHA Token Utility
 * 
 * DEV módban lehetőséget biztosít fix score használatára teszteléshez.
 * Production módban valódi Google reCAPTCHA token-t generál.
 */

/**
 * reCAPTCHA token lekérése (valódi vagy teszt)
 * 
 * @param {Function} executeRecaptcha - Google reCAPTCHA executeRecaptcha függvény
 * @param {string} action - Action név (pl. 'login', 'registration')
 * @returns {Promise<string>} reCAPTCHA token
 */
export async function getRecaptchaToken(executeRecaptcha, action = 'submit') {
    // Ellenőrizzük, hogy van-e teszt score beállítva
    const testScore = process.env.NEXT_PUBLIC_TEST_RECAPTCHA_SCORE;
    
    // Ha van teszt score, használjuk azt (függetlenül a NODE_ENV-től)
    if (testScore) {
        const score = parseFloat(testScore);
        
        // Validáció: score 0.0 és 1.0 között kell legyen
        if (!isNaN(score) && score >= 0.0 && score <= 1.0) {
            console.log(`[TEST] Using test reCAPTCHA score: ${score}`);
            return `dev_bypass_token:${score}`;
        } else {
            console.warn(`[TEST] Invalid test score: ${testScore}, using real reCAPTCHA`);
        }
    }
    
    // Normál eset: valódi Google reCAPTCHA token
    if (!executeRecaptcha) {
        console.error('executeRecaptcha function not available');
        throw new Error('reCAPTCHA not initialized');
    }
    
    try {
        const token = await executeRecaptcha(action);
        return token;
    } catch (error) {
        console.error('reCAPTCHA execution failed:', error);
        throw error;
    }
}

/**
 * reCAPTCHA hiba ellenőrzése
 * 
 * @param {Object} error - Axios error objektum
 * @returns {boolean} true ha reCAPTCHA hiba
 */
export function isRecaptchaError(error) {
    const message = error?.response?.data?.message || error?.message || '';
    return (
        message.includes('reCAPTCHA') ||
        message.includes('Biztonsági ellenőrzés') ||
        error?.response?.data?.errcode === 'recaptcha_low_score' ||
        error?.response?.status === 403
    );
}

/**
 * Formázott reCAPTCHA hibaüzenet
 * 
 * @param {Object} error - Axios error objektum
 * @returns {Object} { header, message, duration }
 */
export function getRecaptchaErrorMessage(error) {
    const responseData = error?.response?.data;
    const message = responseData?.message || 'Ismeretlen hiba történt';
    
    if (message.includes('< 0.3') || message.includes('score')) {
        return {
            header: 'Biztonsági ellenőrzés sikertelen',
            message: 'Gyanús aktivitást észleltünk. Kérjük, próbáld újra később.',
            duration: 6000
        };
    }
    
    if (message.includes('timeout') || message.includes('invalid-input-response')) {
        return {
            header: 'reCAPTCHA hiba',
            message: 'A biztonsági ellenőrzés időtúllépés miatt sikertelen. Kérjük, frissítsd az oldalt és próbáld újra.',
            duration: 6000
        };
    }
    
    return {
        header: 'Hiba történt',
        message: message,
        duration: 5000
    };
}

/**
 * Challenge (email verification) szükséges-e a válasz alapján?
 * 
 * @param {Object} response - Backend API válasz
 * @returns {boolean} true ha challenge szükséges
 */
export function needsEmailVerification(response) {
    return !!(
        response?.response?.needs_email_verification ||
        response?.response?.message_status === 'pending_verification'
    );
}

/**
 * reCAPTCHA score lekérése a válaszból (debug célra)
 * 
 * @param {Object} response - Backend API válasz
 * @returns {number|null} Score érték vagy null
 */
export function getRecaptchaScore(response) {
    return response?.response?.recaptcha_score || null;
}
